import React, { useEffect, useMemo, useState } from "react";
import { taskApi } from "../../backend/client";
import {
  TaskDialog,
  TaskDialogActions,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const INIT = {
  visitDate: "",
  visitTime: "",
  address: "",
  contactName: "",
  contactPhone: "",
  accessNotes: "",
  notes: "",
};

const allEmpty = (obj) =>
  Object.values(obj).every((value) => String(value ?? "").trim() === "");

const HomeVisitForm = ({ taskId, onClose, onSave }: any) => {
  const [form, setForm] = useState(INIT);
  const [saving, setSaving] = useState(false);
  const canSave = useMemo(() => !saving, [saving]);

  useEffect(() => {
    const load = async () => {
      if (!taskId) return;
      const { data, error } = await taskApi.get(taskId);

      if (error) {
        console.error("Load home_visit_info error:", error.message);
        return;
      }

      const info =
        data?.home_visit_info && typeof data.home_visit_info === "object"
          ? data.home_visit_info
          : {};

      setForm({
        visitDate: String(info.visitDate ?? ""),
        visitTime: String(info.visitTime ?? ""),
        address: String(info.address ?? ""),
        contactName: String(info.contactName ?? ""),
        contactPhone: String(info.contactPhone ?? ""),
        accessNotes: String(info.accessNotes ?? ""),
        notes: String(info.notes ?? ""),
      });
    };

    load();
  }, [taskId]);

  const update = (key) => (event) =>
    setForm((previous) => ({ ...previous, [key]: event.target.value ?? "" }));

  const handleSave = async () => {
    if (!taskId || saving) return;
    setSaving(true);

    const cleaned = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [
        key,
        String(value ?? "").trim(),
      ]),
    );
    const next = allEmpty(cleaned) ? null : cleaned;

    const { error } = await taskApi.update(taskId, { home_visit_info: next });

    setSaving(false);
    if (error) {
      console.error("Save home_visit_info error:", error.message);
      return;
    }

    onSave?.();
    onClose?.();
  };

  return (
    <TaskDialog
      title="Home Visit"
      description="Visit schedule, address, access notes, and contact details."
      onClose={onClose}
      className="max-w-2xl"
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="home-visit-date"
            label="Date"
            type="date"
            value={form.visitDate}
            onChange={update("visitDate")}
          />
          <TaskTextInput
            id="home-visit-time"
            label="Time"
            type="time"
            value={form.visitTime}
            onChange={update("visitTime")}
          />
          <TaskTextInput
            id="home-visit-address"
            label="Address"
            value={form.address}
            onChange={update("address")}
            placeholder="Street, suburb, state"
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="home-visit-contact-name"
            label="Contact Name"
            value={form.contactName}
            onChange={update("contactName")}
          />
          <TaskTextInput
            id="home-visit-contact-phone"
            label="Contact Phone"
            type="tel"
            value={form.contactPhone}
            onChange={update("contactPhone")}
            placeholder="+61 ..."
          />
          <TaskTextArea
            id="home-visit-access-notes"
            label="Access Notes"
            value={form.accessNotes}
            onChange={update("accessNotes")}
            rows={3}
            placeholder="Parking, gate code, pets, etc."
            className="sm:col-span-2"
          />
          <TaskTextArea
            id="home-visit-general-notes"
            label="General Notes"
            value={form.notes}
            onChange={update("notes")}
            rows={4}
            placeholder="Anything else relevant to the visit..."
            className="sm:col-span-2"
          />
        </div>

        <TaskDialogActions
          onCancel={onClose}
          onSubmit={handleSave}
          disabled={!canSave}
          submitLabel={saving ? "Saving..." : "Save"}
        />
      </div>
    </TaskDialog>
  );
};

export default HomeVisitForm;
